import Phaser from 'phaser';

/** All marks begin at an authoritative event. They never schedule a hit. */
export type BurstKind = 'hit' | 'block' | 'break' | 'dust' | 'swing' | 'thrust' | 'arrow' | 'treat';
interface Burst { kind: BurstKind; x: number; y: number; angle: number; scale: number; power: number; age: number; life: number; toward?: readonly [number, number] }
const LIFE: Record<BurstKind, number> = { hit: 300, block: 430, break: 500, dust: 1100, swing: 230, thrust: 260, arrow: 220, treat: 850 };
const grain = (i: number): number => (i * .61803398875) % 1;

/** One drawing surface, bounded marks, no lights or full-screen filters.
 * Broken, layered strokes keep iron, bronze, linen and plaster distinct. */
export class CombatEffects {
  readonly graphics: Phaser.GameObjects.Graphics;
  #bursts: Burst[] = [];
  constructor(scene: Phaser.Scene) { this.graphics = scene.add.graphics().setDepth(2004); }

  add(kind: BurstKind, x: number, y: number, angle: number, scale: number, power = .5, delay = 0, toward?: readonly [number, number]): void {
    // ponytail: retain the newest 32 marks; expand only if a shipped encounter needs more simultaneous contacts.
    if (this.#bursts.length >= 32) this.#bursts.shift();
    this.#bursts.push({ kind, x, y, angle, scale, power: Math.max(0, Math.min(1, power)), age: -delay, life: LIFE[kind], ...(toward ? { toward } : {}) });
  }

  draw(elapsed: number, reduced: boolean): void {
    if (!this.#bursts.length) return;
    const g = this.graphics.clear();
    for (const b of this.#bursts) b.age += elapsed;
    this.#bursts = this.#bursts.filter(b => b.age < b.life);
    for (const b of this.#bursts) {
      if (b.age < 0) continue;
      const t = b.age / b.life, fade = (1 - t) ** 1.3 * (reduced ? .45 : 1), s = b.scale;
      const travel = reduced ? .35 : 1 - (1 - t) ** 3;
      if (b.kind === 'swing' || b.kind === 'thrust' || b.kind === 'arrow') {
        if (reduced || !b.toward) continue;
        const dx = b.toward[0] - b.x, dy = b.toward[1] - b.y, length = Math.hypot(dx, dy) || 1;
        const ux = dx / length, uy = dy / length, reach = b.kind === 'arrow' ? length : Math.min(length, 68 * s);
        if (b.kind === 'swing') {
          const a = Math.atan2(dy, dx), radius = Math.min(46 * s, reach);
          // A dry brush crescent, broad at the shoulder, narrowing at the tip.
          for (let i = 0; i < 12; i++) {
            const p = a - .9 + i * .12, q = p + .13;
            g.lineStyle((1 - i / 14) * 4 * s, i % 3 ? 0xb4b9b6 : 0xf0e7d2, fade * .6)
              .lineBetween(b.x + Math.cos(p) * radius, b.y + Math.sin(p) * radius * .55,
                b.x + Math.cos(q) * radius, b.y + Math.sin(q) * radius * .55);
          }
        } else {
          // Only the arriving edge catches the light. A full line through the
          // formation reads as a beam and obscures anyone between the fighters.
          const tip = length, tail = Math.max(0, tip - (b.kind === 'arrow' ? 23 : 19) * s);
          for (let i = 0; i < 5; i++) {
            const a = tail + (tip - tail) * i / 5, z = tail + (tip - tail) * (i + .8) / 5;
            const offset = (grain(i + 3) - .5) * 2 * s;
            g.lineStyle((b.kind === 'arrow' ? 1 : 2.4 - i * .35) * s, i % 2 ? 0x929b9c : 0xd4cbb8, fade * .7)
              .lineBetween(b.x + ux * a - uy * offset, b.y + uy * a + ux * offset,
                b.x + ux * z - uy * offset, b.y + uy * z + ux * offset);
          }
        }
      } else if (b.kind === 'hit') {
        const a = b.angle + .45, length = (17 + 18 * b.power) * s;
        // Iron edge, pale contact, then only a small dark-red fleck remains.
        for (let i = 0; i < 5; i++) {
          const offset = (i - 2) * 1.2 * s, reach = length * (1 - Math.abs(i - 2) * .13) / 2;
          g.lineStyle((i === 2 ? 2 : 1) * s, i === 2 ? 0xf0e6d1 : 0x929b9c, fade * .75)
            .lineBetween(b.x - Math.cos(a) * reach + Math.sin(a) * offset, b.y - Math.sin(a) * reach,
              b.x + Math.cos(a) * reach + Math.sin(a) * offset, b.y + Math.sin(a) * reach);
        }
        for (let i = 0; i < 4; i++) {
          const d = (3 + 12 * grain(i + 1)) * s * travel, a = b.angle + (grain(i + 5) - .5) * 1.5;
          g.fillStyle(i % 2 ? 0x632f29 : 0x9b5140, fade * .7)
            .fillEllipse(b.x + Math.cos(a) * d, b.y + Math.sin(a) * d + 7 * s * travel * travel, (2 + b.power * 2) * s, 1.5 * s);
        }
      } else if (b.kind === 'block' || b.kind === 'break') {
        const bronze = b.kind === 'block', reach = (bronze ? 31 : 38) * s;
        const glint = (bronze ? 10 : 14) * s * (1 - t) ** 2;
        g.lineStyle(1.5 * s, 0xd4b071, fade).lineBetween(b.x - glint * .4, b.y + glint * .3, b.x + glint * .3, b.y - glint * .5);
        for (let i = 0; i < 13; i++) {
          const a = b.angle + Math.PI + (grain(i + 1) - .5) * (bronze ? 2.8 : 4.6);
          const r = reach * travel * (.3 + .7 * grain(i + 3)), tail = (2 + 5 * grain(i + 7)) * s * (1 - t);
          const x = b.x + Math.cos(a) * r, y = b.y + Math.sin(a) * r * .7 + 13 * s * travel * travel;
          g.lineStyle((1 + grain(i + 4)) * s, bronze ? (i % 3 ? 0xc09a58 : 0xf0d89c) : (i % 2 ? 0x8b8e8b : 0xd7cbb4), fade)
            .lineBetween(x, y, x + Math.cos(a) * tail, y + Math.sin(a) * tail);
        }
      } else if (b.kind === 'dust') {
        // Low, irregular plaster dust. No expanding magic ring, no glowing corpse.
        for (let i = 0; i < 14; i++) {
          const a = i * 2.4, r = (8 + 24 * grain(i + 2)) * s * travel;
          const x = b.x + Math.cos(a) * r, y = b.y + Math.sin(a) * r * .26 - (2 + 4 * grain(i + 6)) * s * travel;
          g.fillStyle(i % 3 ? 0xc3b59b : 0x736b5d, fade * .13)
            .fillEllipse(x, y, (10 + 17 * grain(i + 4)) * s * (.6 + travel), (3 + 5 * grain(i + 5)) * s);
        }
      } else {
        // Tight linen strips at the hands: care is physical work, not a healing aura.
        for (let i = 0; i < 3; i++) g.lineStyle((2 - i * .3) * s, 0xdcd4bf, fade * .8)
          .lineBetween(b.x - 7 * s, b.y + i * 3 * s, b.x + 7 * s, b.y + (i * 3 - 3) * s);
      }
    }
    if (!this.#bursts.length) g.clear();
  }
}
